<template>
  <div class="samsung-configurator">
    <GridContainer>
      <Heading
        tag="h2"
        align="center"
        uppercase
        spaces-top
      >
        Dein Galaxy Watch Konfigurator
      </Heading>
      <Paragraph align="center">
        Finde deine passende Smartwatch und das perfekte Armband
      </Paragraph>

      <client-only>
        <SmartwatchConfigurator
          :smartwatch-groups="groups"
          :accessories-promotions="accessoriesPromotion"
          @select="selectArticleNumber"
        />
      </client-only>
    </GridContainer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { GridContainer, Heading, Paragraph } from '@i22/td-longtail-vue-components';
import SmartwatchConfigurator, {
  type SmartwatchGroups,
} from '~/components/smartwatch-config/smartwatch-configurator.vue';

const groups: SmartwatchGroups[] = [
  {
    name: 'Galaxy Watch Ultra',
    entries: [
      {
        articleNumber: '99935607',
        color: 'Titanium Gray',
      },
      {
        articleNumber: '99935609',
        color: 'Titanium Silver',
      },
      {
        articleNumber: '8806095687902',
        color: 'Titanium White',
        isPreselected: true,
      },
    ],
  },
  {
    name: 'Galaxy Watch7',
    entries: [
      // lte + 40mm
      {
        articleNumber: '8806095685373',
        connectivity: 'LTE',
        size: '40 mm',
        color: 'Green',
      },
      {
        articleNumber: '99935598',
        connectivity: 'LTE',
        size: '40 mm',
        color: 'Cream',
      },

      // bt + 40mm
      {
        articleNumber: '8806095661797',
        connectivity: 'Bluetooth',
        size: '40 mm',
        color: 'Green',
      },
      {
        articleNumber: '8806095662121',
        connectivity: 'Bluetooth',
        size: '40 mm',
        color: 'Cream',
      },

      // lte + 44mm
      {
        articleNumber: '99935602',
        connectivity: 'LTE',
        size: '44 mm',
        color: 'Green',
        isPreselected: true,
      },
      {
        articleNumber: '8806095685489',
        connectivity: 'LTE',
        size: '44 mm',
        color: 'Cream',
      },

      // bt + 44mm
      {
        articleNumber: '8806095660547',
        connectivity: 'Bluetooth',
        size: '44 mm',
        color: 'Green',
      },
      {
        articleNumber: '8806095660721',
        connectivity: 'Bluetooth',
        size: '44 mm',
        color: 'Cream',
      },
    ],
  },

  {
    name: 'Galaxy Watch FE',
    entries: [
      {
        articleNumber: '8806095735702',
        color: 'Black',
      },
      {
        articleNumber: '8806095735719',
        color: 'Pink',
        isPreselected: true,
      },
      {
        articleNumber: '8806095735665',
        color: 'Silver',
      },
    ],
  },
  {
    name: 'Galaxy Watch4 Classic',
    entries: [
      // lte + 42mm
      {
        articleNumber: 'SMR885FZKADBT',
        connectivity: 'LTE',
        size: '42 mm',
        color: 'Black',
      },
      {
        articleNumber: 'SMR885FZSADBT',
        connectivity: 'LTE',
        size: '42 mm',
        color: 'Silber',
        isPreselected: true,
      },

      // lte + 46mm
      {
        articleNumber: 'SMR895FZKADBT',
        connectivity: 'LTE',
        size: '46 mm',
        color: 'Black',
      },
      {
        articleNumber: 'SMR895FZSADBT',
        connectivity: 'LTE',
        size: '46 mm',
        color: 'Silber',
      },

      // bt + 42mm
      {
        articleNumber: 'SMR880NZKADBT',
        connectivity: 'Bluetooth',
        size: '42 mm',
        color: 'Black',
      },
      {
        articleNumber: '',
        connectivity: 'Bluetooth',
        size: '42 mm',
        color: 'Silber',
      },

      // bt + 46mm
      {
        articleNumber: 'SMR890NZKADBT',
        connectivity: 'Bluetooth',
        size: '46 mm',
        color: 'Black',
      },
      {
        articleNumber: 'SMR890NZSADBT',
        connectivity: 'Bluetooth',
        size: '46 mm',
        color: 'Silber',
      },
    ],
  },
];

const selectedArticleNumber = ref<string | null>(null);

const accessoriesPromotion = computed(() => {
  if (selectedArticleNumber.value === null) return [];
  // ultra
  if (['99935607', '99935609', '8806095687902'].includes(selectedArticleNumber.value)) {
    return [
      {
        articleNumber: '8806095631080',
        name: 'PeakForm',
      },
      {
        articleNumber: '8806095631127',
        name: 'Trail',
      },
    ];
  }

  return [
    {
      articleNumber: '8806095625324',
      name: 'Fabric M/L',
    },
    {
      articleNumber: '8806095625218',
      name: 'Athleisure S/M',
    },
    {
      articleNumber: '8806095649566',
      name: 'Athleisure M/L',
    },
    {
      articleNumber: '8806095630984',
      name: 'Sport S/M',
    },
    {
      articleNumber: '8806095630854',
      name: 'Sport M/L',
    },
  ];
});

const selectArticleNumber = (articleNumber: string | null) => {
  selectedArticleNumber.value = articleNumber;
};
</script>

<style lang="scss">
@use 'assets/base' as *;
.samsung-configurator {
  background-color: var(--color-background-subtle);
  padding-block: $grid-base * 2;

  @include mq($mq-sm) {
    padding-block: $grid-base * 4;
  }
}
</style>
